import React, { useState, useEffect } from 'react';
import styles from './ApproverPreview.module.css';
import InvoiceDetails from '../../components/InvoiceDetails/InvoiceDetails';
import { useParams } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import RejectButton from '../../components/RejectButton/RejectButton';
import ApproveButton from '../../components/ApproveButton/ApproveButton';
import Modal from 'react-modal';
import useApproval from '../../hooks/useApproval';
import axios from 'axios';
import keycloak from '../../keycloak';
import useAssignApprover from '../../hooks/useAssignApprover';
import AssignApproverModal from '../../components/AssignApproverModal/AssignApproverModal';

export default function ApproverPreview() {
    const { category, id } = useParams();
    const navigate = useNavigate();
    const [approved, setApproved] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isReassigning, setIsReassigning] = useState(false);
    const [formData, setFormData] = useState({});
    const [fetchedData, setFetchedData] = useState({});
    const [pdfBlobUrl, setPdfBlobUrl] = useState('');
    const invoiceID = id || sessionStorage.getItem("selectedInvoiceID");
    const selectedInvoiceStatus = sessionStorage.getItem("selectedInvoiceStatus");
    const approverActiveTab = sessionStorage.getItem("approverActiveTab");
    const currentApproverEmails = String(formData?.approverEmails || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    const currentReviewerEmail = currentApproverEmails[currentApproverEmails.length - 1] || '';
    const { data, approveInvoices, modifyInvoice, refetch } = useApproval(invoiceID);
    const {
        assigningInvoiceID,
        selectedApproverEmail,
        setSelectedApproverEmail,
        availableApprovers,
        assignApproverLoading,
        approversLoading,
        openAssignApproverModal,
        closeAssignApproverModal,
        assignSelectedApprover,
    } = useAssignApprover({
        enabled: true,
        excludeEmails: currentReviewerEmail ? [currentReviewerEmail] : [],
    });
    const [pageLoading, setLoading] = useState(true);

    const customModalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'var(--theme-bg-elevated)',
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
    };

    useEffect(() => {
        if (approved) {
            setIsModalOpen(true);
        }
    }, [approved]);

    const closeModal = () => {
        sessionStorage.removeItem("selectedInvoiceID");
        navigate('/approve-invoices');
        setIsModalOpen(false);
    };

    const goBack = () => {
        sessionStorage.removeItem("selectedInvoiceID");
        navigate('/approve-invoices');
    };

    const normalizedStatus = String(formData?.status || '').replace(/\s+/g, '').toLowerCase();
    const normalizedSelectedStatus = String(selectedInvoiceStatus || '').replace(/\s+/g, '').toLowerCase();
    const isFinalizedFromSelectedStatus = normalizedSelectedStatus === 'approved' || normalizedSelectedStatus === 'rejected';
    const isFinalizedFromActiveTab = approverActiveTab === 'approved' || approverActiveTab === 'rejected';
    const isFinalized = normalizedStatus === 'approved' || normalizedStatus === 'rejected' || isFinalizedFromSelectedStatus || isFinalizedFromActiveTab;
    const isApprovedStatus =
        normalizedStatus === 'approved' || normalizedSelectedStatus === 'approved' || approverActiveTab === 'approved';
    const previousApproverNote = String(formData?.rejectNote || '').trim();
    const approverNames = String(formData?.approverEmails || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((approverEmail) => {
            const localPart = approverEmail.split('@')[0] || approverEmail;
            return localPart
                .replace(/[._-]+/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase());
        })
        .join(', ');
    const statusText =
        isApprovedStatus
            ? 'Approved'
            : normalizedStatus === 'rejected' || normalizedSelectedStatus === 'rejected' || approverActiveTab === 'rejected'
                ? 'Rejected'
                : '';

    useEffect(() => {
        const fetchPDF = async (url) => {
            try {
                const response = await axios.get(url, {
                    headers: {
                        'Authorization': `Bearer ${keycloak.token}`
                    },
                    responseType: 'blob'
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);
                setPdfBlobUrl(blobUrl);
            } catch (error) {
                console.error('Error fetching PDF:', error);
            }
        };

        if (data && data.getInvoice) {
            const invoiceData = data.getInvoice.body;
            setFormData(invoiceData);
            setLoading(false);
            setFetchedData(invoiceData);
            const pdfUrl = invoiceData.invoiceFilename
                ? invoiceData.invoiceFilename.startsWith("http")
                    ? invoiceData.invoiceFilename
                    : `http://localhost:8000${invoiceData.invoiceFilename}`
                : null;
            if (pdfUrl) {
                fetchPDF(pdfUrl);
            }

         }
    }, [data]);

    useEffect(() => {
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
    }, [pdfBlobUrl]);
    // //TODO: File Preview - set to pdf to get image from db
    // let filename = "1s27kyqpio.pdf";
    // const document = require(`../../../../back-end/uploads/${filename}`);

    const handleApprove = async () => {
        if (isFinalized) return;
        const modifications = {};
        const oldValues = {};
        for (const field in formData) {
            if (formData.hasOwnProperty(field) && fetchedData.hasOwnProperty(field)) {
                //Check if field was modified
                if (formData[field] !== fetchedData[field]) {
                    modifications[field] = formData[field];
                    oldValues[field] = fetchedData[field];
                }
            }
        }
        //If any field was modified, update database
        if (Object.keys(modifications).length !== 0) {
            // Required arguments
            modifications["invoiceID"] = invoiceID.toString();
            modifications["oldValues"] = JSON.stringify(oldValues);
            modifications["originalAmount"] = formData["originalAmount"].toString();
            modifications["currency"] = formData["currency"];
            try {
                await modifyInvoice(modifications);
                try {
                    await approveInvoices(invoiceID);
                    setApproved(true);
                } catch (error) {
                    console.error("Error approving invoice:", error);
                }
            } catch(err) {
                console.error("Error modifying invoice:", err);
            }
            
        } else {
            try {
                // Approve without calling modifyInvoices
                await approveInvoices(invoiceID);
                setApproved(true);
            } catch (error) {
                console.error("Error approving invoice:", error);
            }
        }

    };

    const handleReject = () => {
        if (isFinalized) return;
        const modifications = {};
        const oldValues = {};
        for (const field in formData) {
            if (formData.hasOwnProperty(field) && fetchedData.hasOwnProperty(field)) {
                //Check if field was modified
                if (formData[field] !== fetchedData[field]) {
                    modifications[field] = formData[field];
                    oldValues[field] = fetchedData[field];
                }
            }
        }

        oldValues["rejectNote"] = fetchedData["rejectNote"];
        sessionStorage.setItem("oldValues", JSON.stringify(oldValues));
        sessionStorage.setItem("modification", JSON.stringify(modifications));
        sessionStorage.setItem("originalAmount", formData["originalAmount"].toString());
        sessionStorage.setItem("currency", formData["currency"]);
        navigate(`/approve-invoices/reject/${category}/${id}`);
    };

    const handleReassignInvoice = () => {
        if (isFinalized) return;
        openAssignApproverModal(invoiceID);
    };

    const handleReassignSubmit = async () => {
        setIsReassigning(true);
        try {
            await assignSelectedApprover(async () => {
                await refetch?.();
            });
        } finally {
            setIsReassigning(false);
        }
    };

    if (!pageLoading)
    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>

                <div style={{ width: "30px" }}>
                    <IconButton onClick={goBack} sx={{ color: 'var(--theme-text)' }}>
                        <ArrowBackIosNew />
                    </IconButton>
                </div>
                <div className={styles.title}>
                    {isFinalized ? `Invoice No. ${id}` : `Reviewing Invoice no. ${id}`}
                </div>
            </div>

            <div className={styles.invoiceContainer}>

                <div className={styles.preview}>
                    {pdfBlobUrl ? (
                        <embed
                            src={pdfBlobUrl}
                            type="application/pdf"
                            className={styles.previewFile}
                        />
                    ) : (
                        <div className={styles.previewPlaceholder}>
                            No bill uploaded!
                        </div>
                    )}
                </div>

                <div className={styles.formContainer}>
                    <div className={styles.details}>
                        <div className={styles.items}>
                            <InvoiceDetails onApproverPage = {true} formData={formData} setFormData={setFormData}/>
                        </div>
                    </div>

                    <div className={styles.commentSection}>
                        <h3>Supplier Note</h3>
                        <div className={styles.commentContainer}>
                            <div className={styles.messageItem}>
                                {formData.userNote || 'No supplier note.'}
                            </div>
                        </div>
                    </div>

                    {previousApproverNote && (
                        <div className={styles.commentSection}>
                            <h3>Previous Approver Note</h3>
                            <div className={styles.commentContainer}>
                                <div className={styles.messageItem}>
                                    {previousApproverNote}
                                </div>
                            </div>
                        </div>
                    )}

                    {!isFinalized && (
                        <div className={styles.approvalInfo}>
                            <div style={{ padding: "10px" }}> <ApproveButton handleApprove={handleApprove} /> </div>
                            <div style={{ padding: "10px" }}> <RejectButton handleReject={handleReject} /> </div>
                            <div className={styles.reassignButtonContainer}>
                                <button
                                    type="button"
                                    className={styles.reassignButton}
                                    onClick={handleReassignInvoice}
                                >
                                    REASSIGN
                                </button>
                            </div>
                        </div>
                    )}
                    {isFinalized && (
                        <div className={styles.invoiceStatusWrap}>
                            <div className={styles.invoiceApproverInfo}>
                                <span className={styles.invoiceStatusLabel}>Approver(s):</span>
                                <span className={styles.invoiceApproverValue}>{approverNames || 'Unassigned'}</span>
                            </div>
                            <div className={styles.invoiceStatusInfo}>
                                <span className={styles.invoiceStatusLabel}>Status:</span>
                                <span className={`${styles.invoiceStatusValue} ${isApprovedStatus ? styles.invoiceStatusApproved : styles.invoiceStatusRejected}`}>
                                    {statusText}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

            </div>
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                style={customModalStyles}
                contentLabel="Submission Result"
            >
                <button className={styles.closeButton} onClick={closeModal}>
                    <span className={styles.closeIcon}>x</span>
                </button>
                <h2 className={styles.successMessage}>You have successfully approved the invoice.</h2>
            </Modal>
            <AssignApproverModal
                isOpen={Boolean(assigningInvoiceID)}
                invoiceID={assigningInvoiceID}
                title="Reassign Invoice"
                description={`Current approver${currentApproverEmails.length > 1 ? 's' : ''}: ${currentApproverEmails.length > 0 ? currentApproverEmails.join(', ') : 'Unassigned'}. Select a new in-office approver to take over this invoice.`}
                selectedApproverEmail={selectedApproverEmail}
                onApproverChange={setSelectedApproverEmail}
                availableApprovers={availableApprovers}
                onCancel={closeAssignApproverModal}
                onAssign={handleReassignSubmit}
                isAssigning={isReassigning || assignApproverLoading}
                isApproversLoading={approversLoading}
                cancelLabel="Cancel"
                confirmLabel="Reassign"
                loadingLabel="Reassigning..."
            />
        </div>
    )
}
